import { create } from 'zustand'
import type { ListParams, SortDirection } from '@/types/api/common'

/**
 * Client-only UI state: the active filter/search/sort/page selection per
 * listing screen, so a filter survives navigating to a record and back.
 * It is cleared whenever the user moves to a different module (client
 * feedback, 2026-10-08) — see `useClearFiltersOnModuleChange`.
 *
 * This stores the USER'S SELECTION only — never the rows returned by the API.
 * Rows live in the React Query cache keyed by these params.
 */
export type FilterValue = string | number | boolean | null | undefined

export interface ListingFilters {
  readonly page: number
  readonly pageSize?: number
  readonly search: string
  readonly sortBy?: string
  readonly sortDir?: SortDirection
  readonly filters: Record<string, FilterValue>
}

const EMPTY: ListingFilters = { page: 1, search: '', filters: {} }

interface FiltersState {
  readonly byScope: Record<string, ListingFilters>
  readonly get: (scope: string) => ListingFilters
  readonly setSearch: (scope: string, search: string) => void
  readonly setPage: (scope: string, page: number) => void
  readonly setPageSize: (scope: string, pageSize: number) => void
  readonly setSort: (scope: string, sortBy: string | undefined, sortDir: SortDirection) => void
  readonly setFilter: (scope: string, key: string, value: FilterValue) => void
  readonly resetFilters: (scope: string) => void
  /** Drops every scope — used when the user leaves one module for another. */
  readonly clearAll: () => void
}

function patch(
  state: FiltersState,
  scope: string,
  changes: Partial<ListingFilters>,
): Pick<FiltersState, 'byScope'> {
  const current = state.byScope[scope] ?? EMPTY
  return { byScope: { ...state.byScope, [scope]: { ...current, ...changes } } }
}

export const useFiltersStore = create<FiltersState>()((set, get) => ({
  byScope: {},
  get: (scope) => get().byScope[scope] ?? EMPTY,
  // Any filter/search/sort change resets to page 1 — otherwise the user lands
  // on an empty page of a smaller result set.
  setSearch: (scope, search) => set((s) => patch(s, scope, { search, page: 1 })),
  setPage: (scope, page) => set((s) => patch(s, scope, { page })),
  setPageSize: (scope, pageSize) => set((s) => patch(s, scope, { pageSize, page: 1 })),
  setSort: (scope, sortBy, sortDir) => set((s) => patch(s, scope, { sortBy, sortDir, page: 1 })),
  setFilter: (scope, key, value) =>
    set((s) => {
      const current = s.byScope[scope] ?? EMPTY
      const filters = { ...current.filters }
      if (value === null || value === undefined || value === '') delete filters[key]
      else filters[key] = value
      return patch(s, scope, { filters, page: 1 })
    }),
  // Sort is cleared too, so "Clear filters" returns the grid to its default order.
  resetFilters: (scope) =>
    set((s) =>
      patch(s, scope, { search: '', filters: {}, sortBy: undefined, sortDir: undefined, page: 1 }),
    ),
  clearAll: () => set({ byScope: {} }),
}))

/** Maps stored filter state onto the params every listing endpoint accepts. */
export function toListParams(filters: ListingFilters, defaultPageSize: number): ListParams {
  return {
    page: filters.page,
    pageSize: filters.pageSize ?? defaultPageSize,
    ...(filters.search ? { search: filters.search } : {}),
    ...(filters.sortBy ? { sortBy: filters.sortBy, sortDir: filters.sortDir ?? 'asc' } : {}),
    filters: filters.filters,
  }
}
